import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading, PrimaryButton } from './ui/UI';
/**
 * AI Security Validation — what Periscan validates inside AI apps.
 *
 * Was: a 2-column glass-divided panel listing "Modern companies ship" and
 * "AI app risk realities" with animated chip rows on each side, plus a
 * separate "7 questions Periscan answers" block. Now: two editorial
 * columns, no glass panel, no chips, no animated reveal-from-left. Same
 * content beats — the shipping list, the risks list, the 7 questions.
 */
const shipping = ['LLM-powered products', 'Internal copilots', 'Customer-support bots', 'RAG systems', 'AI agents', 'Workflow automations', 'AI-enabled SaaS features', 'Developer assistants', 'Knowledge-base chatbots'];
const risks = ['Prompt injection', 'Sensitive data leakage', 'RAG authorization failure', 'Unsafe tool invocation', 'Agent over-permissioning', 'System prompt exposure', 'Guardrail drift', 'Cross-tenant data exposure', 'Customer-security-review gaps', 'Unverified AI safety controls'];
const questions = ['Can our AI app leak sensitive data?', 'Can prompt injection bypass instructions?', 'Can a RAG system retrieve data a user should not access?', 'Can an agent call tools it should not use?', 'Are our AI guardrails still working?', 'Can we prove our AI app was tested?', 'Can we produce evidence for customer security reviews?'];
export function AISecurityValidation() {
  return <section id="ai-security" className="relative w-full py-32 border-t border-white/5" data-id="element-161">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-162">
        <SectionLabel number="—" label="AI Security Validation" className="mb-8" data-id="element-163" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-164">
          <div className="lg:col-span-8" data-id="element-165">
            <SectionHeading size="xl" data-id="element-166">
              AI apps are now part of
              <br data-id="element-167" />
              <span className="text-white/40" data-id="element-168">the attack surface.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-4 lg:pt-4" data-id="element-169">
            <p className="text-lg text-white/60 leading-relaxed mb-6" data-id="element-170">
              Validate the AI apps you build before customers, attackers, or
              auditors do. Periscan turns AI-security validation into practical
              evidence.
            </p>
            <div className="p-4 rounded-xl bg-[#1a8fd4]/10 border border-[#1a8fd4]/20" data-id="element-171">
              <p className="text-sm text-white/80 leading-relaxed" data-id="element-172">
                <strong className="text-white" data-id="element-173">Note:</strong> This module is for
                securing the AI apps you build. If you want to safely use
                frontier AI models to secure your environment, explore the{' '}
                <a href="/frontier-gateway" className="text-[#1a8fd4] hover:underline" data-id="element-174">
                  Frontier Gateway
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        {/* Shipping + Risks — two editorial columns, no glass panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-24" data-id="element-175">
          <div className="lg:col-span-6" data-id="element-176">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-177">
              Modern companies ship
            </p>
            <ul className="border-t border-white/5" data-id="element-178">
              {shipping.map((item, i) => <motion.li key={item} initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: i * 0.025
            }} className="border-b border-white/5 py-3.5 text-base text-white/85 flex items-baseline gap-4" data-id="element-179">
                  <span className="font-mono text-xs text-[#1a8fd4]/60 tracking-[0.2em] w-8 shrink-0" data-id="element-180">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {item}
                </motion.li>)}
            </ul>
          </div>

          <div className="lg:col-span-6" data-id="element-181">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1a8fd4] mb-6" data-id="element-182">
              The risks Periscan validates
            </p>
            <ul className="border-t border-[#1a8fd4]/20" data-id="element-183">
              {risks.map((item, i) => <motion.li key={item} initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: i * 0.025
            }} className="border-b border-[#1a8fd4]/15 py-3.5 text-base text-white flex items-baseline gap-4" data-id="element-184">
                  <span className="font-mono text-xs text-[#1a8fd4]/80 tracking-[0.2em] w-8 shrink-0" data-id="element-185">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {item}
                </motion.li>)}
            </ul>
          </div>
        </div>

        {/* Questions Periscan answers */}
        <div className="border-t border-white/10 pt-16" data-id="element-186">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-187">
            Questions Periscan answers
          </p>
          <ul className="space-y-4 mb-12 max-w-3xl" data-id="element-188">
            {questions.map((q, i) => <motion.li key={q} initial={{
            opacity: 0,
            x: -6
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="text-xl md:text-2xl text-white/85 font-medium tracking-tight leading-snug" data-id="element-189">
                <span className="font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em] mr-4" data-id="element-190">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {q}
              </motion.li>)}
          </ul>

          <PrimaryButton href="/early-access" size="lg" data-id="element-191">
            Validate Your AI Apps
          </PrimaryButton>
        </div>
      </div>
    </section>;
}